import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import FadeIn from '../components/FadeIn';
import BrandLogo from '../components/BrandLogo';
import GoogleReviewCTA, { GOOGLE_REVIEW_URL } from '../components/GoogleReviewCTA';
import Seo from '../components/Seo';
import { listarTestemunhosPublicos } from '../services/testemunhos';
import { FOTOS, FOTOS_ESPACO } from '../utils/fotosSite';
import { linkWhatsApp, MENSAGEM_AULA_GRATIS } from '../utils/contactos';
import { registarEvento } from '../utils/analytics';
import ValePresenteNatal from '../components/ValePresenteNatal';
import PopupEvento from '../components/PopupEvento';
import EventoDestaque from '../components/EventoDestaque';

const destaques = [
  { icon: '🧘', foto: FOTOS.yoga, titulo: 'Yoga', desc: 'Encontre equilíbrio e paz interior com as nossas aulas de Yoga para todos os níveis.' },
  { icon: '🤸', foto: FOTOS.pilates, titulo: 'Pilates', desc: 'Fortaleça o seu corpo e melhore a postura com o método Pilates.' },
  { icon: '💆', foto: FOTOS.massagem, titulo: 'Massagens', desc: 'Relaxe profundamente com os nossos tratamentos de massagem terapêutica e relaxante.' },
  { icon: '✨', foto: FOTOS.terapias, titulo: 'Terapias', desc: 'Reiki e outras terapias holísticas para o seu bem-estar integral.' },
];

const diferenciais = [
  { icon: '🌱', texto: 'Ambiente tranquilo e acolhedor' },
  { icon: '🏅', texto: 'Profissionais certificados e experientes' },
  { icon: '📅', texto: 'Horários flexíveis para o seu dia a dia' },
  { icon: '❤️', texto: 'Cuidado personalizado para cada cliente' },
];

export default function Home() {
  const [testemunhos, setTestemunhos] = useState([]);
  const [carregandoTestemunhos, setCarregandoTestemunhos] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(3); // 3 testemunhos de cada vez no computador, 1 no telemóvel
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    let cancelado = false;
    let timeoutId;
    let idleId;

    const carregar = () => {
      listarTestemunhosPublicos()
        .then((dados) => {
          if (!cancelado) setTestemunhos(dados);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelado) setCarregandoTestemunhos(false);
        });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(carregar, { timeout: 1500 });
    } else {
      timeoutId = setTimeout(carregar, 700);
    }

    return () => {
      cancelado = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const atualizar = () => setPorPagina(mq.matches ? 3 : 1);
    atualizar();
    mq.addEventListener('change', atualizar);
    return () => mq.removeEventListener('change', atualizar);
  }, []);

  const totalPaginas = Math.max(1, Math.ceil(testemunhos.length / porPagina));

  useEffect(() => {
    if (totalPaginas <= 1) return;
    const id = setInterval(() => {
      setPagina(prev => (prev + 1) % totalPaginas);
    }, 9000);
    return () => clearInterval(id);
  }, [totalPaginas]);

  useEffect(() => {
    if (pagina > totalPaginas - 1) setPagina(0);
  }, [pagina, totalPaginas]);

  const testemunhosVisiveis = useMemo(
    () => testemunhos.slice(pagina * porPagina, pagina * porPagina + porPagina),
    [testemunhos, pagina, porPagina]
  );

  function origemLabel(origem) {
    if (origem === 'whatsapp') return 'WhatsApp';
    if (origem === 'instagram') return 'Instagram';
    if (origem === 'facebook') return 'Facebook';
    if (origem === 'google') return 'Google';
    return 'Cliente';
  }

  function estrelas(avaliacao) {
    const n = Number(avaliacao) || 0;
    return '★'.repeat(n) + '☆'.repeat(Math.max(0, 5 - n));
  }

  function irParaPagina(idx) {
    setPagina((idx + totalPaginas) % totalPaginas);
  }

  return (
    <div>
      <PopupEvento />
      <Seo description="Yoga, Pilates, massagens e terapias holísticas em Gondifelos. 1ª aula de Yoga ou Pilates experimental e grátis. Agende a sua sessão com acompanhamento personalizado." />

      {/* Hero */}
      <section className="relative text-white py-32 px-4 overflow-hidden" style={{ backgroundColor: '#2E2C29' }}>
        {/* Padrão geométrico subtil */}
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23B89547' fill-opacity='0.07'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        {/* Glow gradients */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 15% 60%, #B89547 0%, transparent 45%), radial-gradient(circle at 85% 20%, #B89547 0%, transparent 40%)`
        }} />
        <div className="relative max-w-4xl mx-auto text-center">
          {/* Tagline accent */}
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="h-px w-8" style={{ backgroundColor: '#B89547' }}></span>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase" style={{ color: '#B89547' }}>Bem-estar · Equilíbrio · Saúde</span>
            <span className="h-px w-8" style={{ backgroundColor: '#B89547' }}></span>
          </div>
          <div className="mb-6 flex justify-center">
            <BrandLogo
              src="/branding/logo-hero.png"
              alt="Marcia Monteiro Wellness"
              imgClassName="h-28 md:h-36 w-auto object-contain"
              fallbackClassName="text-4xl md:text-6xl font-semibold leading-tight"
              light={true}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width={600}
              height={240}
              sizes="(min-width: 768px) 36rem, 20rem"
            />
          </div>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-6 leading-relaxed" style={{ color: '#a09b93' }}>
            Yoga, Pilates, massagens e terapias holísticas para equilíbrio físico e mental,
            com acompanhamento personalizado num ambiente acolhedor.
          </p>
          <a href={linkWhatsApp(MENSAGEM_AULA_GRATIS)} target="_blank" rel="noopener noreferrer"
            onClick={() => registarEvento('clique_whatsapp', { origem: 'home_aula_gratis' })}
            className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full text-sm font-semibold transition hover:opacity-80"
            style={{ backgroundColor: 'rgba(184,149,71,0.15)', border: '1px solid #B89547', color: '#DCCCA0' }}>
            <span>🎁</span>
            <span>1ª aula de Yoga ou Pilates experimental e grátis — marcar agora →</span>
          </a>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contato"
              className="brand-btn px-8 py-3 shadow-lg"
              style={{ backgroundColor: '#B89547', color: '#FEFEFD' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#9A7C38'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#B89547'}
            >
              Marcar Sessão
            </Link>
            <Link to="/servicos"
              className="brand-btn px-8 py-3"
              style={{ border: '1px solid #B89547', color: '#B89547' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#B89547'; e.currentTarget.style.color = '#FEFEFD'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#B89547'; }}
            >
              Os Nossos Serviços
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
            {[
              'Atendimento por marcação',
              'Horários flexíveis',
              'Espaço acolhedor em Gondifelos',
              'Várias modalidades no mesmo local'
            ].map(item => (
              <div key={item} className="rounded-xl border px-4 py-2 text-sm font-medium"
                style={{ borderColor: '#5e584f', backgroundColor: 'rgba(255,255,255,0.03)', color: '#d5d0c8' }}>
                {item}
              </div>
            ))}
          </div>
        </div>
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0 leading-none">
          <svg viewBox="0 0 1440 48" preserveAspectRatio="none" className="w-full h-12 block" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,48 C480,0 960,0 1440,48 L1440,48 L0,48 Z" fill="#FEFEFD"/>
          </svg>
        </div>
      </section>

      <EventoDestaque />

      {/* Destaques de serviços */}
      <section className="defer-render py-20 px-4" style={{ backgroundColor: '#FEFEFD' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#B89547' }}>O que oferecemos</span>
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: '#2E2C29' }}>Os Nossos Serviços</h2>
            <div className="w-12 h-0.5 mx-auto mt-2 mb-4" style={{ backgroundColor: '#B89547' }}></div>
            <p className="text-base max-w-xl mx-auto" style={{ color: '#706b63' }}>Tudo o que precisa para cuidar do seu corpo e da sua mente num só lugar.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {destaques.map((d, i) => (
              <FadeIn key={d.titulo} delay={i * 100}>
                <div
                  className="brand-card card-hover rounded-2xl overflow-hidden text-center border hover:-translate-y-1 h-full"
                  style={{ backgroundColor: '#ECEAE5', borderColor: '#dbd7d0' }}
                >
                  <img src={d.foto} alt={d.titulo} loading="lazy" decoding="async"
                    className="w-full h-56 object-cover" style={{ objectPosition: 'center 30%' }} />
                  <div className="p-6">
                    <h3 className="font-semibold text-lg mb-2" style={{ color: '#2E2C29' }}>{d.icon} {d.titulo}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#706b63' }}>{d.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/servicos" className="text-sm font-semibold transition" style={{ color: '#B89547' }}
              onMouseEnter={e => e.currentTarget.style.color = '#9A7C38'}
              onMouseLeave={e => e.currentTarget.style.color = '#B89547'}
            >
              Ver todos os serviços →
            </Link>
          </div>
        </div>
      </section>

      <ValePresenteNatal />

      {/* O nosso espaço */}
      <section className="defer-render py-20 px-4" style={{ backgroundColor: '#2E2C29' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#B89547' }}>Venha conhecer</span>
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: '#ECEAE5' }}>O Nosso Espaço</h2>
            <div className="w-12 h-0.5 mx-auto mt-2 mb-4" style={{ backgroundColor: '#B89547' }}></div>
            <p className="text-base max-w-xl mx-auto" style={{ color: '#9c9890' }}>Um ambiente calmo e luminoso em Gondifelos, pensado para o seu bem-estar.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {FOTOS_ESPACO.map((f, i) => (
              <FadeIn key={f.alt} delay={i * 100}>
                <img src={f.src} alt={f.alt} loading="lazy" decoding="async"
                  className="w-full aspect-[3/4] object-cover rounded-2xl" />
              </FadeIn>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10">
            <Link to="/galeria" className="text-sm font-semibold transition" style={{ color: '#B89547' }}>Ver galeria completa →</Link>
            <a href="https://www.instagram.com/marcia.monteiro.wellness" target="_blank" rel="noopener noreferrer"
              className="text-sm font-semibold transition" style={{ color: '#B89547' }}>Siga-nos no Instagram →</a>
          </div>
        </div>
      </section>

      {/* Testemunhos */}
      <section className="defer-render py-20 px-4" style={{ backgroundColor: '#F7F5F1' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#B89547' }}>Feedback real</span>
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: '#2E2C29' }}>O Que Dizem os Nossos Clientes</h2>
            <div className="w-12 h-0.5 mx-auto mt-2 mb-4" style={{ backgroundColor: '#B89547' }}></div>
            <p className="text-base" style={{ color: '#706b63' }}>Testemunhos recolhidos diretamente no Instagram e WhatsApp.</p>
          </div>

          {carregandoTestemunhos ? (
            <div className="text-center py-10" style={{ color: '#706b63' }}>A carregar testemunhos...</div>
          ) : !testemunhos.length ? (
            <div className="text-center py-10" style={{ color: '#706b63' }}>Novos testemunhos em breve.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
                {testemunhosVisiveis.map(t => {
                  const aberto = !!expandidos[t.id];
                  return (
                    <FadeIn key={t.id}>
                      <div className="rounded-2xl border p-6 h-full" style={{ backgroundColor: '#FEFEFD', borderColor: '#dbd7d0' }}>
                        <div className="flex items-center justify-between mb-3 text-sm">
                          <span style={{ color: '#B89547' }}>{estrelas(t.avaliacao)}</span>
                          <span className="px-2 py-0.5 rounded-full border text-xs" style={{ borderColor: '#DCCCA0', color: '#9A7C38', backgroundColor: '#FFF9EE' }}>
                            {origemLabel(t.origem)}
                          </span>
                        </div>
                        <p className={`text-sm leading-relaxed whitespace-pre-line ${aberto ? '' : 'line-clamp-6'}`} style={{ color: '#2E2C29' }}>
                          {t.texto}
                        </p>
                        {String(t.texto || '').length > 260 && (
                          <button
                            type="button"
                            onClick={() => setExpandidos(prev => ({ ...prev, [t.id]: !aberto }))}
                            className="mt-2 text-xs font-semibold hover:underline"
                            style={{ color: '#9A7C38' }}
                          >
                            {aberto ? 'Ler menos' : 'Ler mais'}
                          </button>
                        )}
                        <div className="mt-4 text-sm font-semibold" style={{ color: '#2E2C29' }}>{t.nome_cliente}</div>
                      </div>
                    </FadeIn>
                  );
                })}
              </div>

              {totalPaginas > 1 && (
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    type="button"
                    aria-label="Testemunhos anteriores"
                    onClick={() => irParaPagina(pagina - 1)}
                    className="w-9 h-9 rounded-full border text-lg transition"
                    style={{ borderColor: '#DCCCA0', color: '#9A7C38', backgroundColor: '#FEFEFD' }}
                  >
                    ‹
                  </button>
                  <div className="flex gap-2">
                    {Array.from({ length: totalPaginas }).map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        aria-label={`Ir para a página ${i + 1} de testemunhos`}
                        onClick={() => irParaPagina(i)}
                        className="w-2.5 h-2.5 rounded-full transition"
                        style={{ backgroundColor: i === pagina ? '#B89547' : '#d8d2c8' }}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    aria-label="Próximos testemunhos"
                    onClick={() => irParaPagina(pagina + 1)}
                    className="w-9 h-9 rounded-full border text-lg transition"
                    style={{ borderColor: '#DCCCA0', color: '#9A7C38', backgroundColor: '#FEFEFD' }}
                  >
                    ›
                  </button>
                </div>
              )}

              <p className="mt-8 text-center text-sm" style={{ color: '#706b63' }}>
                Já é cliente?{' '}
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline" style={{ color: '#9A7C38' }}>
                  Deixe a sua opinião no Google →
                </a>
              </p>
            </>
          )}
        </div>
      </section>

      {/* Porque nos escolher */}
      <section className="defer-render py-20 px-4" style={{ backgroundColor: '#ECEAE5' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
              <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: '#B89547' }}>Porquê nós</span>
              <span className="h-px w-6" style={{ backgroundColor: '#B89547' }}></span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-2" style={{ color: '#2E2C29' }}>Porquê escolher-nos?</h2>
            <div className="w-12 h-0.5 mx-auto mt-2 mb-4" style={{ backgroundColor: '#B89547' }}></div>
            <p className="text-base" style={{ color: '#706b63' }}>A sua saúde e bem-estar são a nossa prioridade.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {diferenciais.map((d, i) => (
              <FadeIn key={d.texto} delay={i * 100}>
                <div className="brand-card card-hover flex items-center gap-4 rounded-xl px-6 py-5 border h-full"
                  style={{ backgroundColor: '#FEFEFD', borderColor: '#dbd7d0' }}>
                  <span className="text-3xl">{d.icon}</span>
                  <span className="font-medium" style={{ color: '#2E2C29' }}>{d.texto}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <div className="defer-render">
        <GoogleReviewCTA subtle />
      </div>

      {/* CTA final */}
      <section className="defer-render py-20 px-4 text-white text-center" style={{ backgroundColor: '#2E2C29' }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4" style={{ color: '#ECEAE5' }}>Pronto para começar?</h2>
          <p className="text-lg mb-2" style={{ color: '#9c9890' }}>Dê o primeiro passo em direção ao seu bem-estar. Contacte-nos hoje.</p>
          <p className="text-sm mb-10" style={{ color: '#B89547' }}>🎁 Ainda não experimentou? A primeira aula de Yoga ou Pilates é grátis.</p>
          <Link to="/contato"
            className="brand-btn px-10 py-3 shadow-lg inline-block"
            style={{ backgroundColor: '#B89547', color: '#FEFEFD' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#9A7C38'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#B89547'}
          >
            Falar Connosco
          </Link>
        </div>
      </section>
    </div>
  );
}
